import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button, Modal, message, Tooltip } from 'antd';
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import pako from 'pako';
import ENVIRONMENT from '../../../environments';
import CustomerMainLayout from '../CustomerComponents/CustomerMainLayout';
import AIEditorModalViewer from '../CustomerComponents/AIEditorModalViewer/AIEditorModalViewer';
import CompareSiloView from '../CustomerComponents/AIEditorModalViewer/CompareSiloView';
import GenerateAiVideoModal from '../CustomerComponents/GenerateAiVideoModal/GenerateAiVideoModal';
import SubscriptionUpgradeManager from '../CustomerComponents/SubscriptionUpgradeManager/SubscriptionUpgradeManager';
import OnboardingTourModal from '../../OnboardingTour/OnboardingTourModal';
import { setCookie, getCookie } from '../Utilities';

const CUSTOMER_USERNAME = localStorage.getItem('username');

const getIframeUserContextPayload = () => {
  const keys = [
    'username',
    'company_id',
    'shared_entities',
    'all3d_jwt_token',
    'user_role',
    'is_admin',
    'is_msprovider',
    'managed_company_id',
    'super_admin_username',
    'is_ai_customer',
  ];

  return keys.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const MigratedAiAssetCreator = ({ location }) => {
  const iframeRef = React.useRef(null);
  const migratedPortalBaseUrl = (ENVIRONMENT.MIGRATED_PORTAL_URL || process.env.REACT_APP_MIGRATED_PORTAL_URL || 'http://localhost:5173').replace(/\/$/, '');
  const iframeSrc = `${migratedPortalBaseUrl}/ai-asset-creator${location?.search || ''}`;

  const targetOrigin = useMemo(() => {
    try {
      return new URL(migratedPortalBaseUrl).origin;
    } catch (error) {
      return '*';
    }
  }, [migratedPortalBaseUrl]);

  // --- Modal state ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageData, setSelectedImageData] = useState(null);
  const [compareSiloModalOpen, setCompareSiloModalOpen] = useState(false);
  const [imageDisplayLoader, setImageDisplayLoader] = useState(false);
  const [deleteModal, setDeleteModal] = useState(undefined);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [collaborateLoader, setCollaborateLoader] = useState(false);
  const [generateVideoModalOpen, setGenerateVideoModalOpen] = useState(false);
  const [selectedVideoData, setSelectedVideoData] = useState(null);

  // --- Subscription/credits state ---
  const [subscriptionUpgradeManagerModel, setSubscriptionUpgradeManagerModel] = useState(false);
  const [currentRequestType, setCurrentRequestType] = useState('ai_model');
  const [cardStatus, setCardStatus] = useState(0);

  // --- Onboarding tour state ---
  const [onboardingTourVisible, setOnboardingTourVisible] = useState(false);

  // Show onboarding tour on first visit
  useEffect(() => {
    const hasViewedOnboardingTour = getCookie('ai_creator_onboarding_tour_completed');
    if (!hasViewedOnboardingTour) {
      const timer = setTimeout(() => {
        setOnboardingTourVisible(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleOnboardingTourClose = () => {
    setOnboardingTourVisible(false);
    setCookie('ai_creator_onboarding_tour_completed', 'true', 365);
  };

  const handleOnboardingTourSkip = () => {
    setOnboardingTourVisible(false);
    setCookie('ai_creator_onboarding_tour_completed', 'true', 365);
  };

  // --- Helpers ---
  const sendRefreshToIframe = useCallback(() => {
    const iframeWindow = iframeRef.current && iframeRef.current.contentWindow;
    if (iframeWindow) {
      iframeWindow.postMessage({ type: 'ALL3D_REFRESH_ACTIVITY' }, targetOrigin);
    }
  }, [targetOrigin]);

  const loadUserRequestStatus = useCallback(() => {
    const payload = {
      username: localStorage.getItem('username'),
      request_type: 'ai_model',
      action: 'check',
      category: '',
    };
    axios.post(ENVIRONMENT.PAYMENT_GATEWAY, payload).then((res) => {
      setCardStatus(res.data['card_status']);
      const iframeWindow = iframeRef.current && iframeRef.current.contentWindow;
      if (iframeWindow) {
        iframeWindow.postMessage({ type: 'ALL3D_REFRESH_USER_REQUEST_STATUS' }, targetOrigin);
      }
      });
  }, []);

  const handleVideoGenerationSuccess = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.postMessage({ type: 'ALL3D_VIDEO_GENERATION_SUCCESS' }, targetOrigin);
  }, [targetOrigin]);

  const postUserContext = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.postMessage(
      {
        type: 'ALL3D_USER_CONTEXT',
        payload: getIframeUserContextPayload(),
      },
      targetOrigin
    );
  }, [targetOrigin]);

  // --- Modal callbacks ---
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedImageData(null);
    setCompareSiloModalOpen(false);
  };

  const closeCompareModal = () => {
    setCompareSiloModalOpen(false);
    setSelectedImageData(null);
  };

  const goToNextImage = () => {
    if (!selectedImageData || !selectedImageData.renders) return;
    setImageDisplayLoader(true);
    const renders = selectedImageData.renders;
    const currentIndex = selectedImageData.renderIndex || 0;

    const nextIndex = (currentIndex + 1) >= renders.length ? 0 : currentIndex + 1;
    const renderDetails = renders[nextIndex];

    if (renderDetails) {
      const imageUrl = ENVIRONMENT.getBaseURL() + renderDetails.uri;

      setSelectedImageData({
        ...selectedImageData,
        selectedImageUrl: imageUrl,
        renderIndex: nextIndex,
        render_id: renderDetails['render_id'],
        request_type: renderDetails['request_type'],
        asset_type: renderDetails['asset_type'],
        resolution: renderDetails['resolution'],
        isFavourite: renderDetails['isFavourite'],
        favouriteId: renderDetails['favouriteId'],
        display_name: renderDetails['display_name'],
        is_edit_request: renderDetails['request_type'] == 'edit_request',
        edit_number: renderDetails['edit_number'],
        final_number: renderDetails['final_number'],
        base_number: renderDetails['base_number'],
        image_prompt: renderDetails['image_prompt'],
        render_submitted_on: renderDetails['render_submitted_on'],
        original_input_image_uri: renderDetails['original_input_image_uri'],
        video_prompt: renderDetails['video_prompt'],
        props_image_uris: renderDetails['render_props_uris'] ? renderDetails['render_props_uris'] : selectedImageData.default_props_image_uris,
        is_3d_scene_image: renderDetails['ai_scene_id'] && renderDetails['request_type'] === 'more_generations',
      });

      if (renderDetails.asset_type !== 'ai_video') {
        const img = new window.Image();
        img.onload = () => {
          setImageDisplayLoader(false);
        };
        img.src = imageUrl;
      } else {
        setImageDisplayLoader(false);
      }
    } else {
      setImageDisplayLoader(false);
    }
  };

  const goToPreviousImage = () => {
    if (!selectedImageData || !selectedImageData.renders) return;
    setImageDisplayLoader(true);
    const renders = selectedImageData.renders;
    const currentIndex = selectedImageData.renderIndex || 0;

    const prevIndex = (currentIndex - 1) < 0 ? renders.length - 1 : currentIndex - 1;
    const renderDetails = renders[prevIndex];

    if (renderDetails) {
      const imageUrl = ENVIRONMENT.getBaseURL() + renderDetails.uri;

      setSelectedImageData({
        ...selectedImageData,
        selectedImageUrl: imageUrl,
        renderIndex: prevIndex,
        render_id: renderDetails['render_id'],
        request_type: renderDetails['request_type'],
        asset_type: renderDetails['asset_type'],
        resolution: renderDetails['resolution'],
        isFavourite: renderDetails['isFavourite'],
        favouriteId: renderDetails['favouriteId'],
        display_name: renderDetails['display_name'],
        is_edit_request: renderDetails['request_type'] == 'edit_request',
        edit_number: renderDetails['edit_number'],
        final_number: renderDetails['final_number'],
        base_number: renderDetails['base_number'],
        image_prompt: renderDetails['image_prompt'],
        render_submitted_on: renderDetails['render_submitted_on'],
        original_input_image_uri: renderDetails['original_input_image_uri'],
        video_prompt: renderDetails['video_prompt'],
        props_image_uris: renderDetails['render_props_uris'] ? renderDetails['render_props_uris'] : selectedImageData.default_props_image_uris,
        is_3d_scene_image: renderDetails['ai_scene_id'] && renderDetails['request_type'] === 'more_generations'
      });

      if (renderDetails.asset_type !== 'ai_video') {
        const img = new window.Image();
        img.onload = () => {
          setImageDisplayLoader(false);
        };
        img.src = imageUrl;
      } else {
        setImageDisplayLoader(false);
      }
    } else {
      setImageDisplayLoader(false);
    }
  };

  const markAiImageFavorite = (obj) => {
    const isVideo = obj?.asset_type === 'ai_video' || obj?.image_type === 'ai_video';
    const payload = {
      action: 'add_favorite',
      entity_id: obj.render_id,
      entity_type: 'product',
      image_type: isVideo ? 'ai_video' : 'ai',
      favorited_by: CUSTOMER_USERNAME,
      image_name: isVideo ? 'Untitled' : obj?.render_id.toString() + '.jpg',
    };

    axios.post(ENVIRONMENT.FAVOURITES_CONTROLLER, payload).then(() => {
      // Update local modal state
      setSelectedImageData((prev) =>
        prev ? { ...prev, isFavourite: true } : prev
      );
      sendRefreshToIframe();
    });
  };

  const unmarkAiImageFavorite = (obj) => {
    const payload = {
      action: 'remove_favorite',
      favorite_id: obj?.favouriteId,
      favorited_by: CUSTOMER_USERNAME,
    };
    axios.post(ENVIRONMENT.FAVOURITES_CONTROLLER, payload).then(() => {
      setSelectedImageData((prev) =>
        prev ? { ...prev, isFavourite: false, favouriteId: null } : prev
      );
      sendRefreshToIframe();
    });
  };

  const updatedRenderNameInImagesList = (imageData, updatedDisplayName) => {
    if (selectedImageData && selectedImageData.render_id === imageData.render_id) {
      setSelectedImageData((prev) => ({
        ...prev,
        display_name: updatedDisplayName,
      }));
    }
    sendRefreshToIframe();
  };

  const handleSubmitForReview = (renderIndex, item) => {
    message.loading('Submitting for review...');
    const render = item.renders[renderIndex];

    const payload = {
      action: 'add_review_image',
      submitted_by: CUSTOMER_USERNAME,
      product_id: item.product_id,
    };

    if (render?.asset_type == 'ai_video' || render?.image_type == 'ai_video') {
      payload['video_id'] = render.render_id;
      payload['action'] = 'add_review_video';
    } else {
      payload['image_id'] = render.render_id;
    }

    axios
      .post(ENVIRONMENT.REVIEW_IMAGE_CONTROLLER, payload)
      .then(() => {
        message.success('Image submitted for review successfully!');
        sendRefreshToIframe();
      })
      .catch((error) => {
        message.error('Error submitting image for review');
        console.error(error);
      });
  };

  const handleCollaborate = async (renderIndex, item) => {
    message.loading('Collaborating on image...');
    const render = item.renders[renderIndex];

    let prompt_text = item?.flux_prompt || item?.prompt_text;

    const toBase64Url = (uint8) => {
      let binary = '';
      for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
      return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    };
    const compressState = (obj) => {
      const json = JSON.stringify(obj || {});
      const deflated = pako.deflate(json);
      return toBase64Url(deflated);
    };
    const collaborateState = {
      img: item.input_image_uri || '',
      imgInspection: item.inspiration_image_uri || '',
      inspiration_id: item.inspiration_id || null,
      prompt: item.prompt_text || '',
      props: item.props || [],
    };
    const collaborateParams = new URLSearchParams();
    collaborateParams.set('ai_image', 'true');
    collaborateParams.set('productId', item.product_id);
    if (item.job_id) collaborateParams.set('job_id', String(item.job_id));
    if (item.inspiration_id != null) collaborateParams.set('inspiration_id', String(item.inspiration_id));
    collaborateParams.set('state', compressState(collaborateState));

    if (render.individual_collab_id) {
      collaborateParams.set('individual_collab', 'true');
      window.location.href = `/collaborate-tool/${render.individual_collab_id}?${collaborateParams.toString()}`;
    } else {
      setCollaborateLoader(true);

      const userName = localStorage.getItem('username');

      const payload = {
        image_id: render.render_id,
        display_name: render.display_name || 'Untitled',
        camera_name: render.uri,
        thumbnail: render.uri.replace(/(\.[^.]+)$/, '_512$1'),
        created_by: userName,
        last_modified_by: userName,
        render_path: render.uri,
        state: {},
      };

      const res = await axios.post(ENVIRONMENT.CREATE_COLLABORATE_ENTITY, payload);

      collaborateParams.set('individual_collab', 'true');
      window.location.href = `/collaborate-tool/${res.data.body.collaboration_entity_id}?${collaborateParams.toString()}`;
      setCollaborateLoader(false);
    }
  };

  const handleDeleteClick = (image) => {
    setDeleteModal(image);
  };

  const deleteImage = () => {
    setDeleteLoader(true);
    let payload = {
      action: (deleteModal.asset_type === 'ai_video' || deleteModal.image_type === 'ai_video') ? 'delete_ai_video' : 'delete_ai_image',
      id: deleteModal.render_id,
    };

    axios
      .post(ENVIRONMENT.AI_MODEL_HELPER, payload)
      .then(() => {
        message.info('Image Deleted.');
      })
      .catch(() => {
        message.error('Error in deleting Image');
      })
      .finally(() => {
        handleModalClose();
        setDeleteModal(undefined);
        setDeleteLoader(false);
        sendRefreshToIframe();
      });
  };

  const renderDelete = (x) => {
    return (
      <Tooltip title={<span className="manrope f-12 white">Delete</span>}>
        <Button
          onClick={() => handleDeleteClick(x)}
          className="manrope f-14 w-500 lh-22 ai-action-btn ai-action-btn-red"
          icon={<DeleteOutlined />}
        />
      </Tooltip>
    );
  };

  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel('ai_image_edit_channel');
      bc.onmessage = (event) => {
        console.log('Received BroadcastChannel message:', event.data);
        const { type, payload } = event.data || {};
        if (type === 'AI_IMAGE_EDIT_SUBMITTED') {
          const iframeWindow = iframeRef.current?.contentWindow;
          if (iframeWindow) {
            iframeWindow.postMessage({ type, payload }, targetOrigin);
          }
        }
      };
    } catch (e) {
      console.warn('BroadcastChannel not supported:', e);
    }
    return () => {
      if (bc) bc.close();
    };
  }, [targetOrigin]);

  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel('ai_scene_render_channel');
      bc.onmessage = (event) => {
        const { type, payload } = event.data || {};
        if (type === 'AI_SCENE_RENDER_REQUESTED') {
          const iframeWindow = iframeRef.current?.contentWindow;
          if (iframeWindow) {
            iframeWindow.postMessage({ type, payload }, targetOrigin);
          }
        }
      };
    } catch (e) {
      console.warn('BroadcastChannel ai_scene_render_channel not supported:', e);
    }
    return () => {
      if (bc) bc.close();
    };
  }, [targetOrigin]);

  // --- postMessage listener ---
  useEffect(() => {
    const onMessage = (event) => {
      if (targetOrigin !== '*' && event.origin !== targetOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const { type } = event?.data || {};

      if (type === 'ALL3D_USER_CONTEXT_REQUEST') {
        postUserContext();
      } else if (type === 'ALL3D_OPEN_EDITOR_MODAL') {
        const { imageData, compareSiloModalOpen: compareSilo } = event.data.payload;
        setSelectedImageData(imageData);
        setCompareSiloModalOpen(compareSilo || false);
        if (!compareSilo) {
          setIsModalOpen(true);
        }
      } else if (type === 'ALL3D_OUT_OF_CREDITS') {
        const { requestType } = event.data.payload || {};
        if (requestType) setCurrentRequestType(requestType);
        loadUserRequestStatus();
        setSubscriptionUpgradeManagerModel(true);
      } else if (type === 'ALL3D_OPEN_ONBOARDING_TOUR') {
        setOnboardingTourVisible(true);
      } else if (type === 'ALL3D_OPEN_GENERATE_VIDEO_MODAL') {
        const { imageUrl, imageId } = event.data.payload || {};
        setSelectedVideoData({ imageUrl, imageId });
        setGenerateVideoModalOpen(true);
      } else if (type === 'ALL3D_OPEN_NEW_TAB') {
        const url = event.data.url || '';
        window.open(url, '_blank');
      } else if (type === 'ALL3D_NAVIGATE') {
        const url = event.data.url || '';
        // Rewrite iframe's /ai-asset-creator paths to the parent's actual route
        const parentPath = location?.pathname || '/ai-asset-creator';
        window.location.href = url.replace(/^\/ai-asset-creator(?=\?|$)/, parentPath);
      } else if (event.data?.type === 'ALL3D_REPLACE_URL') {
        window.history.replaceState({}, '', event.data.url);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postUserContext, targetOrigin, loadUserRequestStatus]);

  return (
    <CustomerMainLayout selected="26" remove_global_padding={true}>
      <div style={{ width: '100%', height: '100%' }}>
        <iframe
          ref={iframeRef}
          title="AI Asset Creator"
          src={iframeSrc}
          onLoad={postUserContext}
          style={{
            width: '100%',
            height: '100%',
            border: '0',
            display: 'block',
          }}
        />
      </div>

      <AIEditorModalViewer
        from="activity"
        isOpen={isModalOpen}
        onClose={handleModalClose}
        imageData={selectedImageData}
        compareSiloModalOpen={compareSiloModalOpen}
        imageDisplayLoader={imageDisplayLoader}
        setCompareSiloModalOpen={setCompareSiloModalOpen}
        goToNextImage={goToNextImage}
        goToPreviousImage={goToPreviousImage}
        markFavorite={markAiImageFavorite}
        unmarkFavorite={unmarkAiImageFavorite}
        updatedRenderNameInImagesList={updatedRenderNameInImagesList}
        renderDelete={renderDelete}
        handleSubmitForReview={handleSubmitForReview}
        handleCollaborate={handleCollaborate}
      />

      {/* Standalone Compare Modal - opened directly from image cards */}
      {!isModalOpen && (
        <CompareSiloView
          isOpen={compareSiloModalOpen}
          onClose={closeCompareModal}
          closeAllModals={closeCompareModal}
          imageData={selectedImageData}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        destroyOnClose={true}
        className="modal-share-success"
        width={600}
        visible={deleteModal}
        closable={false}
        footer={[
          <span className="justify-in-center" key="footer">
            <Button
              style={{ textAlign: 'center' }}
              disabled={deleteLoader}
              className="modal-okay-gray square font-14"
              onClick={() => setDeleteModal(undefined)}
            >
              Cancel
            </Button>
            <Button
              style={{ textAlign: 'center' }}
              disabled={deleteLoader}
              className="modal-okay square font-14"
              onClick={() => deleteImage()}
            >
              {deleteLoader ? (
                <span>
                  Deleting <LoadingOutlined spin />
                </span>
              ) : (
                <span> Okay </span>
              )}
            </Button>
          </span>,
        ]}
        centered={true}
      >
        <div
          style={{
            margin: '0px auto 0px auto',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div>
            <h2 className="manrope f-16 black-00 w-600">
              You're about to delete a {(deleteModal?.asset_type === 'ai_video' || deleteModal?.image_type === 'ai_video') ? 'Video' : 'Lifestyle Image'}
            </h2>
            <p className="manrope f-14 black-55">
              This will remove your {(deleteModal?.asset_type === 'ai_video' || deleteModal?.image_type === 'ai_video') ? 'video' : 'lifestyle image'}. Are you sure you want to delete?
            </p>
          </div>
        </div>
      </Modal>

      <SubscriptionUpgradeManager
        subscriptionUpgradeManagerModel={subscriptionUpgradeManagerModel}
        setSubscriptionUpgradeManagerModel={setSubscriptionUpgradeManagerModel}
        cardStatus={cardStatus}
        loadUserRequestStatus={loadUserRequestStatus}
        requestType={currentRequestType}
      />

      <OnboardingTourModal
        visible={onboardingTourVisible}
        onClose={handleOnboardingTourClose}
        onSkip={handleOnboardingTourSkip}
      />

      <GenerateAiVideoModal
        visible={generateVideoModalOpen}
        onCancel={() => {
          setGenerateVideoModalOpen(false);
          setSelectedVideoData(null);
        }}
        imageUrl={selectedVideoData?.imageUrl}
        image_id={selectedVideoData?.imageId}
        onSuccess={async () => {
          handleVideoGenerationSuccess();
        }}
      />
    </CustomerMainLayout>
  );
};

export default MigratedAiAssetCreator;
